import { useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight, Check, LoaderCircle, LockKeyhole, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { FormFields } from './FormFields';
import { PrivacyDialog } from './PrivacyDialog';
import { Reveal } from './Motion';

export const ContactForm = () => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());
  const successRef = useRef(null);
  useEffect(() => { if (status === 'success') successRef.current?.focus({ preventScroll: true }); }, [status]);

  const submit = async (event) => {
    event.preventDefault();
    if (status === 'sending') return;
    const data = Object.fromEntries(new FormData(event.currentTarget));
    if (!data.first_name.trim() || !data.last_name.trim()) { setError('Inserisci nome e cognome validi.'); return; }
    setError('');
    setStatus('sending');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
        body: JSON.stringify({ ...data, privacy_consent: data.privacy_consent === 'on', submission_id: submissionId }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.detail === 'string' ? result.detail : 'Controlla i dati inseriti e riprova.');
      setStatus('success');
      toast.success('Contatto salvato correttamente.');
    } catch (err) {
      setStatus('idle');
      setError(err.name === 'AbortError' || err instanceof TypeError ? 'Connessione non disponibile. I dati restano nel form: riprova tra poco.' : err.message);
    } finally { clearTimeout(timeout); }
  };

  return (
    <section id="contatto" className="contact-section" aria-labelledby="contact-heading" data-testid="contact-form-section">
      <div className="page-width contact-layout">
        <Reveal className="contact-intro">
          <span className="eyebrow" data-testid="contact-section-label"><span className="section-number">02 /</span> RESTIAMO IN CONTATTO</span>
          <h2 id="contact-heading" data-testid="contact-heading">Lascia il tuo<br /><span>contatto.</span></h2>
          <p className="contact-subtitle" data-testid="contact-form-subtitle">Compila il form per essere ricontattato<br className="desktop-break" /> direttamente da Eugenio Fossat.</p>
          <div className="contact-intro-bottom"><span className="intro-mail-icon" aria-hidden="true"><Mail size={25} strokeWidth={1.25} /></span><p data-testid="direct-email-hint">Preferisci scrivermi direttamente?<a href="mailto:e.fossat@composite-research.com" data-testid="form-direct-email-link">Invia un’email <ArrowUpRight size={15} /></a></p></div>
        </Reveal>
        <Reveal className="form-container" delay={0.12}>
          {status === 'success' ? (
            <div className="success-state" role="status" data-testid="contact-success-panel">
              <span className="success-icon"><Check size={32} strokeWidth={1.5} /></span>
              <h3 ref={successRef} tabIndex={-1} data-testid="contact-success-heading">Grazie per il tuo contatto.</h3>
              <p data-testid="contact-success-message">I tuoi dati sono stati salvati correttamente.</p>
              <Button type="button" className="action-button primary-button" onClick={() => { setStatus('idle'); setSubmissionId(crypto.randomUUID()); }} data-testid="send-another-contact-button">Invia un altro contatto<ArrowRight size={17} /></Button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" aria-busy={status === 'sending'}>
              <p className="form-required-note" data-testid="required-fields-note"><span>*</span> Campi obbligatori</p>
              <fieldset disabled={status === 'sending'}>
                <legend className="sr-only">I tuoi dati di contatto</legend>
                <FormFields />
                <div className="privacy-field"><label className="consent-label" htmlFor="privacy_consent" data-testid="privacy-consent-label"><input type="checkbox" name="privacy_consent" id="privacy_consent" required data-testid="privacy-consent-checkbox" /><span>Autorizzo il trattamento dei miei dati personali ai fini del ricontatto.<span className="required-star"> *</span></span></label><PrivacyDialog /></div>
                {error && <p className="form-error" role="alert" data-testid="contact-form-error">{error}</p>}
                <Button type="submit" className="action-button primary-button submit-button" disabled={status === 'sending'} data-testid="submit-contact-button">{status === 'sending' ? <><LoaderCircle className="spin" size={18} />Salvataggio in corso…</> : <>Invia contatto<ArrowUpRight size={19} /></>}</Button>
              </fieldset>
              <p className="form-security-note" data-testid="form-privacy-note"><LockKeyhole size={12} />I tuoi dati, solo per ricontattarti.</p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};